// context/FilterContext.js
"use client";

import React, { createContext, useContext } from "react";
import useLocalStorage from "@/app/hooks/useLocalStorage";
import useExtendedCarDataListener from "@/hooks/useExtendedCarDataListener";

const FilterContext = createContext();

export const useFilter = () => {
  const ctx = useContext(FilterContext);
  if (!ctx) {
    throw new Error("useFilter must be used within a FilterProvider");
  }
  return ctx;
};

export function FilterProvider({ children }) {
  const { carData: allCars = [], loading } =
    useExtendedCarDataListener("extended");

  // Deine Filter-States (useLocalStorage)
  const [selectedBrands, setSelectedBrands] = useLocalStorage(
    "filters_brands",
    []
  );
  const [regFrom, setRegFrom] = useLocalStorage("filters_regFrom", null);
  const [regTo, setRegTo] = useLocalStorage("filters_regTo", null);
  const [brandQuery, setBrandQuery] = useLocalStorage("filters_brandQuery", "");
  const [selectedTransmissions, setSelectedTransmissions] = useLocalStorage(
    "filters_transmissions",
    []
  );
  const [selectedFueltypes, setSelectedFueltypes] = useLocalStorage(
    "filters_fueltypes",
    []
  );
  const [selectedWarantys, setSelectedWarantys] = useLocalStorage(
    "filters_warantys",
    []
  );
  const [selectedConditions, setSelectedConditions] = useLocalStorage(
    "filters_conditions",
    []
  );
  const [kmFrom, setKmFrom] = useLocalStorage("filters_kmFrom", null);
  const [kmTo, setKmTo] = useLocalStorage("filters_kmTo", null);
  const [priceFrom, setPriceFrom] = useLocalStorage("filters_priceFrom", null);
  const [priceTo, setPriceTo] = useLocalStorage("filters_priceTo", null);
  const [isSavePopupOpen, setIsSavePopupOpen] = useLocalStorage(
    "filters_savePopup",
    false
  );

  // Filter-Logik
  const filteredCars = allCars.filter((vehicle) => {
    if (selectedBrands.length && !selectedBrands.includes(vehicle.marke))
      return false;
    const year = new Date(
      vehicle.vehicleFeatures.firstRegistrationDate
    ).getFullYear();
    if (regFrom !== null && year < regFrom) return false;
    if (regTo !== null && year > regTo) return false;
    if (
      selectedTransmissions.length &&
      !selectedTransmissions.includes(vehicle.gearbox)
    )
      return false;
    if (
      selectedFueltypes.length &&
      !selectedFueltypes.includes(vehicle.fueltype)
    )
      return false;
    if (
      selectedWarantys.length &&
      !selectedWarantys.includes(vehicle.vehicleFeatures.garantie)
    )
      return false;
    if (
      selectedConditions.length &&
      !selectedConditions.includes(vehicle.carRating)
    )
      return false;

    const km = Number(`${vehicle.kmStand ?? ""}`.replace(/\D/g, "")) || 0;
    if (kmFrom !== null && km < kmFrom) return false;
    if (kmTo !== null && km > kmTo) return false;

    const priceRaw = vehicle?.bidsData?.currentBid ?? vehicle?.startPrice;
    const price = Number(`${priceRaw ?? ""}`.replace(/\D/g, ""));
    if (priceFrom !== null && price < priceFrom) return false;
    if (priceTo !== null && price > priceTo) return false;

    return true;
  });

  const value = {
    allCars,
    filteredCars,
    selectedBrands,
    setSelectedBrands,
    regFrom,
    setRegFrom,
    regTo,
    setRegTo,
    brandQuery,
    setBrandQuery,
    selectedTransmissions,
    setSelectedTransmissions,
    selectedFueltypes,
    setSelectedFueltypes,
    selectedWarantys,
    setSelectedWarantys,
    selectedConditions,
    setSelectedConditions,
    kmFrom,
    setKmFrom,
    kmTo,
    setKmTo,
    priceFrom,
    setPriceFrom,
    priceTo,
    setPriceTo,
    isSavePopupOpen,
    setIsSavePopupOpen,
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}
